import * as Sentry from '@sentry/browser';
// import logException from '@util/js-error';

let accessToken = null;
let asyncAccessToken = null;
let expirationTime = null;
let timer;

// Generate a unique client ID on application load to assist in debugging
let CLIENT_ID_PREFERRED;
try {
  CLIENT_ID_PREFERRED = window.crypto.randomUUID();
} catch (e) {} // eslint-disable-line

export const CLIENT_ID = CLIENT_ID_PREFERRED || new Date().valueOf();

// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
export const parseJwt = token => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
};

const calculateHoursRemaining = () => {
  return (new Date(expirationTime).getTime() - new Date().getTime()) / (60 * 60 * 1000);
};

const handleTokenAPIResponse = async response => {
  // const contentType = response.headers?.get('content-type');
  if (response.status >= 300) {
    // let message;
    // if (contentType === 'application/json') {
    //   message = await response.json();
    // } else {
    //    message = await response.text();
    // }
    //
    const error = new Error('Token API Error');
    //  // Leaving this in in case we want to start reporting
    //  // these errors to sentry again.
    //
    // logException(error, {
    //   message,
    //   status: response.status,
    //   url: '/AuthHosted/OAuth2/Token'
    // });
    throw error;
  } else {
    if (!response.json && response.message) throw new Error(response.message);
    const data = await response.json();
    expirationTime = new Date();
    expirationTime.setSeconds(expirationTime.getSeconds() + data.expires_in);
    return data.access_token;
  }
};

export const getToken = async () => {
  /**
   * If there's no access token or if the current access token will
   * expire within the next 23 hours, fetch a new access token.
   *
   * return a promise so that multiple parallel API requests don't
   * call the token endpoint multiple times.
   */

  if (asyncAccessToken === null || (expirationTime && calculateHoursRemaining() < 23)) {
    // eslint-disable-next-line no-async-promise-executor
    asyncAccessToken = new Promise(async (resolve, reject) => {
      Sentry.addBreadcrumb({
        type: 'debug',
        message: `Refreshing Token: ${accessToken}; Hours remaining: ${calculateHoursRemaining()}`,
        level: 'info'
      });

      const headers = accessToken
        ? {
            Authorization: `Bearer ${accessToken}`
          }
        : {};

      try {
        const response = await fetch('/AuthHosted/OAuth2/Token', { headers });
        const data = await handleTokenAPIResponse(response);
        accessToken = data;
        resolve(data);
      } catch (e) {
        // Leaving this in in case we want to start reporting
        // these errors to sentry again.
        //
        // logException(e);
        reject(e);
      }
    });
  }

  /**
   * Set a heartbeat timer to automatically attempt to refresh
   * the token if no API calls have been made in the last 12 hours.
   * Most of the time this will be a no-op, but in the case where
   * we hit the 23-hour window before a token expires, it will
   * extend the token, keeping the current session valid.
   */
  if (timer) {
    clearTimeout(timer);
  }
  timer = setTimeout(() => getToken(), 12 * 60 * 60 * 1000);

  return asyncAccessToken;
};

/**
 * Generate the `Authorization` header for API calls. In the
 * event the call to get the token fails, don't throw an
 * error in the client. This will allow the APIs to fall
 * back on cookie-based authorization.
 */
export const getHeaders = async (prevHeaders = {}) => {
  try {
    const token = await getToken();

    return {
      ...prevHeaders,
      'Eduphoria-Client-ID': CLIENT_ID,
      Authorization: token && `Bearer ${token}`
    };
  } catch (error) {
    return prevHeaders;
  }
};

export const refreshToken = async () => {
  asyncAccessToken = null;
  return getToken();
};

export default getToken;
